<template>
    <Transition name="fade">
        <PermissionDialog :is-open="showMicPermissionAide" @cancel="handleMicPermissionDialogClick" @click="() => handleMicPermissionDialogClick({ useText: false })" />
    </Transition>
    <RecordingTestDialog :event-slug="coachingSessionUuid" @dismiss="handleTextboxShow" @mic-selected="handleMicSelection" />
    <ConfirmationDialog
        v-if="piiDetected && !piiBlocked"
        v-model:open="showPiiModal"
        title="Sensitive information detected"
        :text="piiMessage"
        confirm-label="Send anyway"
        cancel-label="Go back and edit"
        @confirm="handlePiiModalConfirm"
        @cancel="handlePiiEdited(PII_WARN)" />
    <BaseDialog v-if="piiBlocked" v-model:open="showPiiModal" title="Message blocked">
        <div class="flex flex-col justify-start items-start p-10 gap-8 relative">
            <BaseButton
                theme="neutral"
                class="border-none absolute right-6 top-6 px-3"
                aria-label="Close dialog"
                @click="
                    showPiiModal = false;
                    handlePiiEdited(PII_BLOCK);
                ">
                <i class="bi bi-x text-3xl text-composer-dialog-dismiss" aria-hidden="true"></i>
            </BaseButton>
            <h2 class="title">Message blocked</h2>
            <div class="body text-ink-muted">{{ piiMessage }}</div>
            <BaseButton
                theme="tertiary"
                class="body py-2 min-w-35"
                @click="
                    showPiiModal = false;
                    handlePiiEdited(PII_BLOCK);
                "
                >Go back and edit</BaseButton
            >
        </div>
    </BaseDialog>
    <div aria-live="polite">
        <StatusBadge :is-showing="shouldShowBadge" :text="statusBadgeText" :offset-y="8" />
    </div>
    <form
        v-bind="$attrs"
        ref="chatMessageControls"
        aria-label="Chat message"
        class="p-3 md:pt-6 rounded-lgr bg-surface-raised border border-valence-grey-200 relative"
        :class="{ 'chat-message-controls--disabled': isDisabled }"
        :aria-disabled="isDisabled"
        data-testid="chat-input"
        @submit.prevent>
        <!-- Recording active (new voice composer only) -->
        <template v-if="betterVoiceComposerEnabled && (isRecording || isInitializing)">
            <!-- Waveform area — same slot as the textarea -->
            <div class="min-h-12 mb-2 px-3">
                <div class="flex items-center gap-3 py-3">
                    <span class="recording-dot shrink-0" aria-hidden="true" />
                    <!-- tabular-nums keeps digit widths fixed so the timer doesn't shift horizontally as 0:09 → 0:10 -->
                    <span class="body-semibold text-ink tabular-nums shrink-0" role="timer" aria-live="off">{{ recordingDisplayTime }}</span>
                    <div class="grow overflow-hidden">
                        <VoiceLevels
                            :levels="isInitializing ? [] : recordingAudioLevelHistory"
                            :bar-size="BAR_SIZE.SMALL"
                            :initial-display="VOICE_LEVEL_INITIAL_DISPLAY.OSCILLATING" />
                    </div>
                </div>
            </div>
            <!-- Bottom row — same slot as the button bar -->
            <div class="flex justify-end items-center gap-3 min-h-12">
                <div class="flex gap-3 items-center grow">
                    <button :disabled="isDisabled" type="button" title="Cancel recording" aria-label="Cancel recording" class="side-button group" @click="handleRecordingCancel">
                        <div class="h-10 w-10 flex items-center justify-center rounded-full transition-colors group-hover:bg-red-100">
                            <i class="bi bi-x-lg text-sm text-valence-grey-750 group-hover:text-red-600" aria-hidden="true" />
                        </div>
                    </button>
                </div>
                <button
                    :disabled="isDisabled"
                    type="button"
                    title="Send recording"
                    aria-label="Send recording"
                    class="recording-send-button recording-send-pulse mr-2"
                    @click="recorder.stop()">
                    <i class="bi bi-send-fill text-lg text-ink-inverse rotate-45 -translate-x-0.5" aria-hidden="true" />
                </button>
            </div>
        </template>

        <!-- Normal input -->
        <template v-else>
            <Transition name="fade">
                <div
                    v-if="isOverBody && !isDisabled && !isFileUploadLimit && documentUploadEnabled"
                    class="dropzone flex items-center justify-center gap-4 text-control-accent"
                    :class="[isOverChatBar ? 'bg-dropzone-bg-active' : 'bg-dropzone-bg']">
                    <i class="bi bi-image text-2xl/none" aria-hidden="true" />
                    <p class="body-semibold">Drop files here to add to chat</p>
                </div>
            </Transition>
            <!-- input: text area + transcribing state -->
            <div class="composer-input-area min-h-12 mb-2 px-3 fs-mask" aria-live="polite" aria-relevant="text additions">
                <ul v-if="hasFiles" class="shrink-0 flex justify-start gap-x-4 gap-y-2 flex-wrap p-1 max-h-96 overflow-y-auto pb-2">
                    <!-- @vue-expect-error - FIXME: chatStore is not yet typed -->
                    <li v-for="file in useChatStore.files" :key="file.id">
                        <!-- @vue-expect-error - FIXME: chatStore is not yet typed -->
                        <FileAttachedTag :file="file" :is-removable="!isDisabled" data-testid="chat-control-file-attached-tag" @remove="useChatStore.removeFile(file.id)" />
                    </li>
                </ul>
                <BaseNotification v-if="piiBlocked" theme="error" hide-dismiss class="mb-2">
                    <template #content>
                        <p class="body-small-semibold">{{ piiBannerMessage }}</p>
                    </template>
                </BaseNotification>
                <BaseNotification v-else-if="piiDetected && !piiBlocked" theme="warning" hide-dismiss class="mb-2">
                    <template #content>
                        <p class="body-small-semibold">{{ piiBannerMessage }}</p>
                    </template>
                </BaseNotification>
                <div v-if="isTranscribing" class="flex items-center gap-3 py-3">
                    <span class="gradient-text body-small" role="status"> Transcribing... </span>
                    <LoadingSpinner class="h-4 text-valence-grey-750" />
                </div>
                <template v-else-if="!betterVoiceComposerEnabled && !textboxVisible">
                    <div class="w-full">
                        <VoiceLevels
                            :class="{
                                'opacity-40 py-5': !isRecording && !isInitializing,
                                'opacity-70 py-5': isInitializing,
                                'py-4': isRecording,
                                'cursor-pointer': !isDisabled && !isRecording && !isInitializing,
                            }"
                            class="md:py-4"
                            :levels="isInitializing ? [] : recordingAudioLevelHistory"
                            :bar-size="isRecording ? BAR_SIZE.SMALL : BAR_SIZE.TINY"
                            :initial-display="VOICE_LEVEL_INITIAL_DISPLAY.OSCILLATING"
                            @click="!isDisabled && !isRecording && !isInitializing && (showTextbox = true)" />
                    </div>
                </template>
                <template v-else>
                    <!-- eslint-disable-next-line vuejs-accessibility/label-has-for - label is associated via for="chat-input-textarea" and textarea id -->
                    <label for="chat-input-textarea" class="sr-only">Message input</label>
                    <div class="relative">
                        <PiiHighlightOverlay v-if="piiPatterns" :text="textContent" :warn-spans="warnSpans" :error-spans="errorSpans" />
                        <textarea
                            id="chat-input-textarea"
                            ref="textarea"
                            v-model="textContent"
                            aria-live="off"
                            autocomplete="off"
                            :disabled="isDisabled"
                            :placeholder="textPlaceholder"
                            :maxlength="textareaMaxLength"
                            :class="piiPatterns ? 'relative z-10' : ''"
                            class="w-full max-h-[40vh] bg-transparent focus-visible:outline-hidden body mt-3"
                            @keydown="handleKeyDown"
                            @keydown.enter="handleSendMessage"
                            @paste="handlePaste" />
                    </div>
                    <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
                    <output v-if="charCounterVisible" :class="charCounterColor" class="text-right mt-1 body-small" for="chat-input-textarea">
                        {{ textContent.length }}/{{ textareaMaxLength }}
                    </output>
                </template>
            </div>
            <div class="composer-bar flex justify-between items-center gap-3 min-h-12">
                <div class="flex gap-3 items-center grow">
                    <div class="flex items-center gap-3">
                        <!-- File Attachment -->
                        <BaseTooltip
                            v-if="documentUploadEnabled && organizationDetails?.is_document_upload_enabled"
                            v-model:open="isDocumentUploadTooltipShown"
                            trigger-aria-label="update document tooltip trigger"
                            :delay-duration="hasSeenDocumentUploadFeatureTooltip ? 0 : 10000">
                            <template #trigger>
                                <button
                                    v-if="!isUploading"
                                    :disabled="isDisabled || isTranscribing || isFileUploadLimit"
                                    type="button"
                                    class="side-button"
                                    aria-label="Upload file"
                                    @click="handleFileUploadClick">
                                    <input
                                        ref="fileInput"
                                        data-testid="file-upload-input"
                                        type="file"
                                        aria-hidden
                                        :disabled="isDisabled"
                                        :accept="acceptedFileTypes"
                                        multiple
                                        class="hidden"
                                        @change="handleFileChange" />
                                    <Clip class="legacy-glyph" background-class="side-button-background" foreground-class="side-button-foreground side-button-foreground--fill" />
                                    <ComposerAttachGlyph class="tonal-glyph" />
                                </button>
                                <LoadingSpinner v-else class="min-w-10 h-6 text-valence-grey-750" />
                            </template>
                            <template #content>{{ documentUploadTooltip }}</template>
                        </BaseTooltip>

                        <!-- Dictation toggle -->
                        <BaseTooltip trigger-aria-label="dictation toggle tooltip trigger">
                            <template #trigger>
                                <button
                                    type="button"
                                    :disabled="isDisabled"
                                    :aria-pressed="useChatStore.dictationOn"
                                    :aria-label="useChatStore.dictationOn ? 'Turn off sound' : 'Turn on sound'"
                                    :class="useChatStore.dictationOn ? 'side-button--active' : ''"
                                    class="side-button"
                                    @click="handleDictationToggle">
                                    <VolumeCircle class="legacy-glyph" background-class="side-button-background" foreground-class="side-button-foreground" />
                                    <ComposerSpeakerGlyph class="tonal-glyph tonal-glyph--audio" />
                                </button>
                            </template>
                            <template #content>
                                {{ useChatStore.dictationOn ? "Turn off sound" : "Turn on sound" }}
                            </template>
                        </BaseTooltip>

                        <!-- WorkIQ toggle (Temporary WorkIQ Demo) -->
                        <label v-if="hasWorkiqConnected" for="workiq-toggle" class="flex items-center gap-2 pl-1 cursor-pointer select-none">
                            <span class="body-small-semibold text-valence-grey-750">WorkIQ</span>
                            <BaseSwitch id="workiq-toggle" v-model="useChatStore.useWorkiq" :disabled="isDisabled" size="sm" />
                        </label>
                    </div>

                    <!-- Dictation speed (visible whenever dictation is on) -->
                    <BaseTooltip v-if="useChatStore.dictationOn" trigger-aria-label="dictation speed tooltip trigger">
                        <template #trigger>
                            <button
                                type="button"
                                :disabled="isDisabled"
                                :aria-label="`Playback speed ${useChatStore.dictationSpeed}x. Click to change.`"
                                class="side-button flex items-center justify-center"
                                @click="cycleDictationSpeed">
                                <span class="body-small-bold text-valence-grey-750" aria-hidden="true">{{ useChatStore.dictationSpeed }}x</span>
                            </button>
                        </template>
                        <template #content>Playback speed</template>
                    </BaseTooltip>

                    <!-- Skip dictation (visible while dictation is on; disabled when nothing is playing) -->
                    <BaseTooltip v-if="useChatStore.dictationOn" trigger-aria-label="skip dictation tooltip trigger">
                        <template #trigger>
                            <button
                                type="button"
                                :disabled="isDisabled || !isDictationPlaying"
                                aria-label="Skip current audio"
                                class="side-button flex items-center justify-center"
                                @click="skipDictation">
                                <i class="bi bi-skip-forward text-2xl/none text-valence-grey-750" aria-hidden="true"></i>
                            </button>
                        </template>
                        <template #content>Skip</template>
                    </BaseTooltip>
                </div>

                <!-- New mode: mic shrinks when text is present; send grows from 0 -->
                <div v-if="betterVoiceComposerEnabled" class="flex items-center gap-2 shrink-0">
                    <template v-if="editAndStopGeneratingEnabled && waitingForAssistant">
                        <button
                            type="button"
                            aria-label="Stop generating"
                            data-testid="stop-generating"
                            class="shrink-0 flex items-center justify-center h-12 w-12 rounded-full border-2 border-valence-grey-400 hover:border-valence-grey-600 hover:bg-valence-grey-100 transition-colors"
                            @click="emit('stop-generating')">
                            <i class="bi bi-stop-fill text-xl text-valence-grey-700" aria-hidden="true" />
                        </button>
                    </template>
                    <template v-else>
                        <BaseTooltip v-if="voiceInputEnabled" trigger-aria-label="record voice message tooltip trigger">
                            <template #trigger>
                                <button
                                    :disabled="isDisabled || isTranscribing"
                                    type="button"
                                    aria-label="Record voice message"
                                    data-testid="record-voice"
                                    class="shrink-0 flex items-center justify-center rounded-full bg-accent hover:bg-accent-hover text-ink-inverse transition-[width,height,background-color,opacity] duration-300 ease-out disabled:opacity-50"
                                    :class="canSubmit ? 'h-10 w-10' : 'h-12 w-12'"
                                    @click="handleRecordingStart()">
                                    <i class="bi bi-mic-fill transition-[font-size] duration-300" :class="canSubmit ? 'text-lg' : 'text-2xl'" aria-hidden="true" />
                                </button>
                            </template>
                            <template #content>Record voice message</template>
                        </BaseTooltip>
                        <BaseTooltip trigger-aria-label="submit message tooltip trigger">
                            <template #trigger>
                                <button
                                    :disabled="!canSubmit"
                                    type="button"
                                    aria-label="Send message"
                                    data-testid="text-submit"
                                    class="shrink-0 flex items-center justify-center rounded-full bg-accent hover:bg-accent-hover text-ink-inverse transition-[width,height,opacity,transform,background-color] duration-300 ease-out overflow-hidden disabled:opacity-50"
                                    :class="composerSubmitSizeClass"
                                    @click="handleSendMessage">
                                    <i class="bi bi-send-fill text-base rotate-45 -translate-x-0.5" aria-hidden="true" />
                                </button>
                            </template>
                            <template #content>Send message</template>
                        </BaseTooltip>
                    </template>
                </div>

                <!-- Old mode: submit when in text mode -->

                <!-- Old mode: input toggle -->
                <BaseTooltip v-if="!betterVoiceComposerEnabled && voiceInputEnabled" trigger-aria-label="input toggle tooltip trigger">
                    <template #trigger>
                        <button
                            :disabled="isDisabled || isRecording || isTranscribing"
                            type="button"
                            :aria-label="inputToggleTipText"
                            :aria-pressed="showTextbox"
                            data-testid="input-source"
                            class="side-button group"
                            @click="showTextbox = !showTextbox">
                            <MicrophoneCircle v-if="showTextbox" class="legacy-glyph" background-class="side-button-background" foreground-class="side-button-foreground" />
                            <ComposerMicGlyph v-if="showTextbox" class="tonal-glyph" />
                            <div v-else class="h-10 w-10 flex items-center justify-center transition-colors group-hover:bg-valence-primary-500/25 rounded-full">
                                <i class="bi bi-keyboard text-valence-grey-750 group-hover:text-valence-primary-500 text-3xl/none" aria-hidden="true" />
                            </div>
                        </button>
                    </template>
                    <template #content>
                        {{ inputToggleTipText }}
                    </template>
                </BaseTooltip>

                <button
                    v-if="!betterVoiceComposerEnabled && textboxVisible && editAndStopGeneratingEnabled && waitingForAssistant"
                    type="button"
                    aria-label="Stop generating"
                    data-testid="stop-generating-legacy"
                    class="shrink-0 flex items-center justify-center h-10 w-10 rounded-full border-2 border-valence-grey-400 hover:border-valence-grey-600 hover:bg-valence-grey-100 transition-colors"
                    @click="emit('stop-generating')">
                    <i class="bi bi-stop-fill text-xl text-valence-grey-700" aria-hidden="true" />
                </button>
                <button
                    v-else-if="!betterVoiceComposerEnabled && textboxVisible"
                    title="Submit"
                    aria-label="Send message"
                    :disabled="!canSubmit"
                    type="button"
                    data-testid="text-submit"
                    class="submit-button"
                    @click="handleSendMessage">
                    <ArrowCircle direction="up" background-class="submit-button-background" foreground-class="submit-button-foreground" />
                </button>

                <!-- Old mode: recorder buttons when in voice mode -->
                <div v-if="!betterVoiceComposerEnabled && !textboxVisible" class="shrink-0 flex items-center">
                    <button v-if="isRecording" type="button" title="Cancel" class="px-4 text-valence-grey-750 hover:text-composer-cancel-hover" @click="handleRecordingCancel">
                        <i class="bi bi-x-lg text-sm" aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        :disabled="isDisabled || isInitializing"
                        :class="isRecording ? 'recorder-button--recording' : 'recorder-button--idle'"
                        :aria-pressed="isRecording"
                        class="transition-colors recorder-button body-bold"
                        @click="isRecording ? recorder.stop() : handleRecordingStart()">
                        <div class="h-10 md:h-6 w-10 md:w-6">
                            <StopCircle v-if="isRecording" background-class="md:fill-transparent fill-[#CF1322]" foreground-class="fill-ink-inverse" />
                            <MicrophoneCircle v-else background-class="fill-accent md:fill-transparent" foreground-class="stroke-ink-inverse" />
                        </div>
                        <span class="md:inline hidden">
                            <template v-if="isInitializing">Initializing...</template>
                            <template v-else>{{ recordingButtonText }}</template>
                        </span>
                    </button>
                </div>
            </div>
        </template>
    </form>

    <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption - the dictated text is displayed on screen -->
    <audio id="audioPlayer" ref="audioPlayer" class="hidden"></audio>
</template>

<script setup lang="ts">
import { AudioRecorder, msToDisplayTime } from "/js/AudioRecorder.js";
import { getCookie } from "/js/utils";
import { Capacitor } from "@capacitor/core";
import { usePage } from "@inertiajs/vue3";
import { useDropZone, useIntervalFn, useMediaQuery, useStorage, useTextareaAutosize } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { GlobalProperties, OrganizationDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseSwitch from "~vue/components/BaseSwitch.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import FileAttachedTag from "~vue/components/FileAttachedTag.vue";
import StatusBadge from "~vue/components/StatusBadge.vue";
import VoiceLevels, { BAR_SIZE, INITIAL_DISPLAY as VOICE_LEVEL_INITIAL_DISPLAY } from "~vue/components/VoiceLevels.vue";
import { useChatInputPreference } from "~vue/composables/useChatInputPreference.js";
import { useMicConsent } from "~vue/composables/useMicConsent";
import { usePiiDetection } from "~vue/composables/usePiiDetection";
import { useTimedShow } from "~vue/composables/useTimedShow";
import type { AudioStats } from "~vue/composables/useTranscription";
import { CHAT_EVENT } from "~vue/events";
import ArrowCircle from "~vue/icons/ArrowCircle.vue";
import Clip from "~vue/icons/Clip.vue";
import ComposerAttachGlyph from "~vue/icons/composer/ComposerAttachGlyph.vue";
import ComposerMicGlyph from "~vue/icons/composer/ComposerMicGlyph.vue";
import ComposerSpeakerGlyph from "~vue/icons/composer/ComposerSpeakerGlyph.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import MicrophoneCircle from "~vue/icons/MicrophoneCircle.vue";
import StopCircle from "~vue/icons/StopCircle.vue";
import VolumeCircle from "~vue/icons/VolumeCircle.vue";
import PermissionDialog from "~vue/Onboarding/PermissionDialog.vue";
import RecordingTestDialog from "~vue/RecordingTestDialog.vue";
import { useChatStore } from "~vue/stores/chatStore";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { FILE_UPLOAD_LIMIT } from "~vue/utils/constants";
import { refreshCsrfToken } from "~vue/utils/csrf";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { MARKDOWN_CONTENT_TYPE, normalizeMarkdownFiles } from "~vue/utils/markdownFileUpload";
import { type PatternEntry, PII_BLOCK, PII_WARN } from "~vue/utils/pii/keywordClassifier";
import { withRequestId } from "~vue/utils/requestId";
import { isSessionEndedError, reportSessionEnded, SESSION_ENDED_ERROR_CAUSE, sessionEndedCodeFrom, wasGloballyHandled403 } from "~vue/utils/sessionEnded";
import { WAF_ERROR_CAUSE } from "~vue/utils/wafDetection";
import DOMPurify from "dompurify";
import { computed, inject, nextTick, onMounted, onUnmounted, type Ref, ref, useTemplateRef, watch } from "vue";

import PiiHighlightOverlay from "./PiiHighlightOverlay.vue";
import type { DictationChunk, RecordingChunk } from "./types";

const DOCUMENT_LENGTH_THRESHOLD = 1000;
const DOCUMENT_TYPES = [
    "text/plain",
    "text/csv",
    MARKDOWN_CONTENT_TYPE,
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const ALL_SUPPORTED_FILE_TYPES = [...DOCUMENT_TYPES, ...IMAGE_TYPES];

type ChatMessageControlProps = {
    isTranscribing?: boolean;
    isSending?: boolean;
    showSlowTranscription?: boolean;
    defaultTextInput?: boolean;
    isDisabled?: boolean;
    isPasteDisabled?: boolean;
    userMessageCount?: number;
    documentUploadEnabled?: boolean;
    betterVoiceComposerEnabled?: boolean;
    hideChatVoiceInputEnabled?: boolean;
    placeholder?: string;
    initialValue?: string;
    isFileUploadLimit?: boolean;
    userCoachExchangeId?: string | null;
    textareaMaxLength?: string;
    location?: string;
    piiPatterns?: PatternEntry[] | null;
    waitingForAssistant?: boolean;
    editAndStopGeneratingEnabled?: boolean;
    hasWorkiqConnected?: boolean;
};

const props = withDefaults(defineProps<ChatMessageControlProps>(), {
    isTranscribing: false,
    isSending: false,
    showSlowTranscription: false,
    defaultTextInput: false,
    isDisabled: false,
    isPasteDisabled: false,
    userMessageCount: 0,
    documentUploadEnabled: false,
    betterVoiceComposerEnabled: false,
    hideChatVoiceInputEnabled: false,
    initialValue: "",
    isFileUploadLimit: false,
    userCoachExchangeId: null,
    piiPatterns: null,
    waitingForAssistant: false,
    editAndStopGeneratingEnabled: false,
    hasWorkiqConnected: false,
});

const chatMessageControls = useTemplateRef("chatMessageControls");
// this is the actual drop area where files are processed on drop
const { isOverDropZone: isOverChatBar } = useDropZone(chatMessageControls, {
    onDrop: (files) => {
        const filesLength = files?.length ?? 0;
        if (files && filesLength > 0 && props.documentUploadEnabled && !props.isDisabled) {
            handleUpload(files, "drag_and_drop");
        }
    },
    preventDefaultForUnhandled: true,
});

// this second dropzone triggers on the body element so that the drag an drop area on chat message control can be seen
const { isOverDropZone: isOverBody } = useDropZone(document.body, {
    preventDefaultForUnhandled: true,
});

const hasSeenDocumentUploadFeatureTooltip = useStorage("hasSeenDocumentUploadFeatureTooltip", false);
const userChatInputPreference = useChatInputPreference();

const emit = defineEmits<{
    "recording-start": [];
    "recording-complete": [RecordingChunk];
    "recording-error": [];
    "textbox-show": [];
    "text-keydown": [];
    "text-send": [{ content: string; files?: File[] | null }];
    "text-paste": [{ name: string; content: string; description: string }];
    "upload-error": [string | null];
    "stop-generating": [];
}>();

const { emitter, $feedbackMailto } = inject<GlobalProperties>("globalProperties", {} as GlobalProperties);
const coachingSessionId = inject("coachingSessionId", undefined);
const coachingSessionUuid = inject<string | undefined>("coachingSessionUuid", undefined);
const organizationDetails = inject<Ref<OrganizationDetails>>("organization_details");

const isLgBreakpoint = useMediaQuery("(min-width: 1024px)");

const recorder = ref(new AudioRecorder());
const isInitializing = ref(false);

// template refs
const audioPlayer = useTemplateRef("audioPlayer");

// Dictation
const isDictationPlaying = ref(false);
const dictationChunks = ref<Record<string, Array<DictationChunk>>>({});
const nextDictationChunkId = ref<Partial<DictationChunk>>({});
const skippedMessageIds = new Set<string>();

const applyDictationSpeed = () => {
    if (audioPlayer.value) {
        audioPlayer.value.playbackRate = useChatStore.dictationSpeed;
    }
};

watch(() => useChatStore.dictationSpeed, applyDictationSpeed);

// Recording
const isRecording = ref(false);
const recordingAudioLevelHistory = ref<number[]>([]);
const showMicPermissionAide = ref(false);
const { micConsentBlocks } = useMicConsent();
const IDLE_MIC_RELEASE_MS = 30000;
let idleMicReleaseTimeout: ReturnType<typeof setTimeout> | undefined;
let componentActive = true;
let recordClickTime: number | null = null;
let initToStartMs: number | null = null;

// Recording timer
const recordingElapsedMs = ref(0);
let recordingStartTimestamp: number | null = null;

const recordingDisplayTime = computed(() => {
    const totalSeconds = Math.floor(recordingElapsedMs.value / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
});

const { pause: pauseRecordingTimer, resume: resumeRecordingTimer } = useIntervalFn(
    () => {
        if (recordingStartTimestamp != null) {
            recordingElapsedMs.value = Date.now() - recordingStartTimestamp;
        }
    },
    200,
    { immediate: false },
);

function startRecordingTimer() {
    recordingStartTimestamp = Date.now();
    recordingElapsedMs.value = 0;
    resumeRecordingTimer();
}

function stopRecordingTimer() {
    pauseRecordingTimer();
    recordingStartTimestamp = null;
    recordingElapsedMs.value = 0;
}

// Normally arrives via shared Inertia props, so a new page mounting this composer is gated by
// default. The prop is the override for anonymous feedback chat, where there is no org context.
const voiceInputEnabled = computed(() => !props.hideChatVoiceInputEnabled && !organizationDetails?.value?.is_chat_voice_input_hidden);

// Initialize with stored preference, fallback to defaultTextInput prop
const showTextbox = ref(userChatInputPreference.value !== null ? userChatInputPreference.value : props.defaultTextInput);

// Without voice input the legacy composer has no way back to text, so it stays in textbox mode.
// Reading through this instead of overwriting showTextbox leaves the stored voice preference intact
// for when the flag is turned back off.
const textboxVisible = computed(() => showTextbox.value || !voiceInputEnabled.value);

const recordingButtonText = computed(() => {
    if (isRecording.value) {
        if (isLgBreakpoint.value) {
            return "Stop recording";
        }
        return "Stop";
    }

    if (isLgBreakpoint.value) {
        return "Start Recording";
    }
    return "Record";
});

const inputToggleTipText = computed(() => {
    if (isRecording.value) {
        return "Stop recording to use text";
    }

    if (showTextbox.value) {
        return "Use voice";
    }

    return "Use text";
});

const { show: showBadge, isShowing: isBadgeShowing } = useTimedShow();

const hasFiles = computed(() => useChatStore.files.length > 0);

const shouldShowBadge = computed(() => {
    return isRecording.value || isBadgeShowing.value || isUploading.value;
});

const textPlaceholder = computed(() => {
    if (props.placeholder) {
        return props.placeholder;
    }
    return hasFiles.value ? "How can I help you with this document?" : "Reply to Nadia here";
});

const statusBadgeText = computed(() => {
    if (isUploading.value) {
        return "Uploading files...";
    }

    if (isRecording.value && !isInitializing.value) {
        return "Listening...";
    }

    if (showTextbox.value) {
        return "Switched to text";
    }

    return "Switched to voice";
});

const { textarea, input: textContent } = useTextareaAutosize({ input: props.initialValue });

const { piiResult, piiBlocked, piiDetected, piiMessage, piiBannerMessage, acceptRisk, warnSpans, errorSpans } = usePiiDetection(props.piiPatterns, textContent);
const showPiiModal = ref(false);

watch(piiDetected, (detected) => {
    if (detected) {
        const tier = piiBlocked.value ? PII_BLOCK : PII_WARN;
        logUserInteraction(AmplitudeEventNames.PII_WARNING_BAR_SHOWN, { tier, patterns: piiResult.value?.reason }, coachingSessionId);
    }
});

const canSubmit = computed(() => {
    if (props.isDisabled || props.isSending || isUploading.value) {
        return false;
    }
    if (hasFiles.value) {
        // A file attachment alone is enough to send; text is optional
        return true;
    }
    return !!textContent.value.trim();
});

// In the new composer the send button normally grows out of zero width as the mic shrinks. With no mic
// to trade places with it, it holds the mic's resting size so the composer height doesn't jump.
const composerSubmitSizeClass = computed(() => {
    if (!voiceInputEnabled.value) {
        return "h-12 w-12 opacity-100 scale-100";
    }
    return canSubmit.value ? "h-10 w-10 opacity-100 scale-100" : "h-0 w-0 opacity-0 scale-0";
});

const maxLength = computed(() => {
    if (props.textareaMaxLength == undefined) {
        return;
    }
    return parseInt(props.textareaMaxLength);
});

const charCounterVisible = computed(() => {
    if (!maxLength.value || !textContent.value.length) {
        return false;
    }
    return textContent.value.length >= maxLength.value * 0.8; // Show at 80% threshold
});

const charCounterColor = computed(() => {
    if (!maxLength.value) {
        return "text-ink-muted";
    }
    // Warning color at 90%, normal gray below
    return textContent.value.length >= maxLength.value * 0.9 ? "text-valence-coral-600" : "text-ink-muted";
});

// Fetch file size limits from API (only when uploads are enabled)
const fileSizeLimits = ref<Record<string, number>>({});
const defaultFileSizeLimit = ref(3 * 1024 * 1024); // 3MB default

const { data: fileSizeLimitsData, execute: fetchFileSizeLimits } = useFetch("/api/chat/document/file-size-limits", { immediate: false }).get().json();

if (props.documentUploadEnabled) {
    fetchFileSizeLimits();
}

watch(fileSizeLimitsData, (data) => {
    if (data) {
        fileSizeLimits.value = data.limits || {};
        defaultFileSizeLimit.value = data.default || 3 * 1024 * 1024;
    }
});

watch(showTextbox, (value) => {
    // Save user preference to local storage
    userChatInputPreference.value = value;

    if (value) {
        logUserInteraction(AmplitudeEventNames.TURNED_ON_TEXT_INPUT, {}, coachingSessionUuid);
        recorder.value.releaseStream();

        nextTick(() => {
            textarea.value?.focus();
        });
    } else {
        logUserInteraction(AmplitudeEventNames.TURNED_ON_VOICE_INPUT, {}, coachingSessionUuid);
    }

    showBadge();
});

onMounted(() => {
    logUserInteraction(AmplitudeEventNames.CHAT_INPUT_SHOWN, { mode: textboxVisible.value ? "text" : "voice", location: props.location }, coachingSessionUuid);
    if (!Capacitor.isNativePlatform()) {
        textarea.value?.focus();
    }
    emitter.on(CHAT_EVENT.DICTATION_EVENT, handleDictationEvent);
    emitter.on(CHAT_EVENT.OPEN_TEXT_INPUT, handleTextboxShow);
    emitter.on(CHAT_EVENT.OPEN_VOICE_INPUT, handleVoiceInputShow);
    emitter.on(CHAT_EVENT.SET_MESSAGE_TEXT, handleSetMessageText);
    recorder.value.on("silence", handleRecorderSilence);
    // @ts-expect-error - AudioRecorder.js is not typed, so its instance of mitt is not typed
    recorder.value.on("level", handleRecorderLevel);
    recorder.value.on("start", handleRecorderStart);
    recorder.value.on("stop", handleRecorderStopped);
    // @ts-expect-error - AudioRecorder.js is not typed, so its instance of mitt is not typed
    recorder.value.on("finish", handleRecorderFinished);
    recorder.value.on("initializing", handleInitializing);
    recorder.value.on("initialized", handleInitialized);
    recorder.value.on("initialization-failed", handleInitializationFailed);

    audioPlayer.value?.addEventListener("ended", handleAudioFinished);
    fileInput.value?.addEventListener("cancel", logUploadCanceled);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    setTimeout(() => {
        hasSeenDocumentUploadFeatureTooltip.value = true;
        isDocumentUploadTooltipShown.value = false;
    }, 10000);
});

onUnmounted(() => {
    componentActive = false;
    emitter.off(CHAT_EVENT.DICTATION_EVENT, handleDictationEvent);
    emitter.off(CHAT_EVENT.OPEN_TEXT_INPUT, handleTextboxShow);
    emitter.off(CHAT_EVENT.OPEN_VOICE_INPUT, handleVoiceInputShow);
    emitter.off(CHAT_EVENT.SET_MESSAGE_TEXT, handleSetMessageText);

    clearTimeout(idleMicReleaseTimeout);
    stopRecordingTimer();
    if (isRecording.value) {
        recorder.value.stop({ abort: true });
    }
    recorder.value.releaseStream();

    // Clean up component-specific listeners
    recorder.value.off("silence", handleRecorderSilence);
    // @ts-expect-error - AudioRecorder.js is not typed, so its instance of mitt is not typed
    recorder.value.off("level", handleRecorderLevel);
    recorder.value.off("start", handleRecorderStart);
    recorder.value.off("stop", handleRecorderStopped);
    // @ts-expect-error - AudioRecorder.js is not typed, so its instance of mitt is not typed
    recorder.value.off("finish", handleRecorderFinished);
    recorder.value.off("initializing", handleInitializing);
    recorder.value.off("initialized", handleInitialized);
    recorder.value.off("initialization-failed", handleInitializationFailed);

    // Clean up blob URLs to prevent memory leaks
    if (audioPlayer.value?.src) {
        URL.revokeObjectURL(audioPlayer.value.src);
    }

    audioPlayer.value?.removeEventListener("ended", handleAudioFinished);
    fileInput.value?.removeEventListener("cancel", logUploadCanceled);
    document.removeEventListener("visibilitychange", handleVisibilityChange);

    useChatStore.removeAllFiles();
});

const getNextDictationChunk = () => {
    if (!useChatStore.dictationOn || isRecording.value) {
        return null;
    }
    if (!nextDictationChunkId.value.messageId || !nextDictationChunkId.value.sequenceNumber) {
        return null;
    }
    const messageChunks = dictationChunks.value[nextDictationChunkId.value.messageId];
    return messageChunks?.find((c) => c.sequenceNumber === nextDictationChunkId.value.sequenceNumber);
};

const deleteDictationChunk = (messageId: string, sequenceNumber: number) => {
    if (!dictationChunks.value[messageId]) {
        return;
    }
    dictationChunks.value[messageId] = dictationChunks.value[messageId].filter((c) => c.sequenceNumber !== sequenceNumber);
};

const handleAudioFinished = () => {
    const chunk = getNextDictationChunk();
    if (!chunk) {
        if (isDictationPlaying.value) {
            logUserInteraction(AmplitudeEventNames.COACH_DICTATION_STOP, {}, coachingSessionUuid);
        }
        isDictationPlaying.value = false;
        return;
    }

    // Queue up the chunk to look for after this one finishes, if any
    nextDictationChunkId.value = { messageId: chunk.messageId, sequenceNumber: (chunk.sequenceNumber ?? 0) + 1 };

    // Create a URL for the Blob and set it as the src of the audio element
    // Revoke old blob URL to prevent memory leak from accumulating blob references
    if (audioPlayer.value?.src && chunk.blob) {
        URL.revokeObjectURL(audioPlayer.value.src);
        audioPlayer.value.src = URL.createObjectURL(chunk.blob);
    }

    // Now that the blob is on the audio player, we can delete this chunk to free up memory
    if (chunk.messageId && chunk.sequenceNumber) {
        deleteDictationChunk(chunk.messageId, chunk.sequenceNumber);
    }

    applyDictationSpeed();

    audioPlayer.value
        ?.play()
        ?.then(() => {
            // Autoplay started.
            if (!isDictationPlaying.value) {
                logUserInteraction(AmplitudeEventNames.COACH_DICTATION_START, {}, coachingSessionUuid);
            }
            isDictationPlaying.value = true;
        })
        .catch(() => {
            // Autoplay was prevented because the user hasn't take an action.
            // TODO: consider showing a "play" button so that user can start playback.
            isDictationPlaying.value = false;
            logUserInteraction(AmplitudeEventNames.COACH_DICTATION_PREVENTED, {}, coachingSessionUuid);
        });
};

const convertB64Mp3ToAudioBlob = (audioStream: string) => {
    const byteCharacters = atob(audioStream);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: "audio/mp3" });
};

const handleDictationEvent = (data: { data: { message_id: string; dictation_count: number; audio_stream: string } }) => {
    if (!useChatStore.dictationOn) {
        // To save memory, don't bother storing dictation chunks if the user is not using dictation
        return;
    }

    const messageId = data.data.message_id;

    if (skippedMessageIds.has(messageId)) {
        return;
    }
    const sequenceNumber = data.data.dictation_count;
    const blob = convertB64Mp3ToAudioBlob(data.data.audio_stream);
    const chunk: DictationChunk = { messageId, sequenceNumber, blob };

    if (dictationChunks.value[messageId]) {
        dictationChunks.value[messageId].push(chunk);
    } else {
        dictationChunks.value[messageId] = [chunk];
    }

    if (sequenceNumber === 1) {
        // This is the first chunk of what is presumably the newest/latest message, so cancel any
        // currently playing dictations and queue up this one.
        stopDictation();
        nextDictationChunkId.value = { messageId, sequenceNumber };
    }

    if (!isDictationPlaying.value && messageId === nextDictationChunkId.value.messageId && sequenceNumber === nextDictationChunkId.value.sequenceNumber) {
        // Either this is the first chunk of a new message, or we had paused the audio while waiting
        // on this chunk, so start it immediately.
        handleAudioFinished(); // Starts the new chunk
    }
};

const handleTextboxShow = () => {
    if (props.isDisabled) {
        return;
    }

    showTextbox.value = true;
    emit("textbox-show");
};

const handleVoiceInputShow = () => {
    if (props.isDisabled || !voiceInputEnabled.value) {
        return;
    }

    showTextbox.value = false;
    handleRecordingStart();
};

const handleSetMessageText = (text: string) => {
    textContent.value = text;
    showTextbox.value = true;
    nextTick(() => {
        textarea.value?.focus();
    });
};

const doSend = () => {
    const clean = DOMPurify.sanitize(textContent.value, { FORBID_TAGS: ["a"] });
    emit("text-send", { content: clean, files: useChatStore.files });
    useChatStore.removeAllFiles();
    textContent.value = "";
};

const handleSendMessage = (event: Event) => {
    if (event instanceof KeyboardEvent && !event.shiftKey) {
        event.preventDefault();
    }

    if (!canSubmit.value) {
        return;
    }

    const isValidTrigger = (event instanceof KeyboardEvent && !event.shiftKey) || !(event instanceof KeyboardEvent);
    if (!isValidTrigger) {
        return;
    }

    if (piiBlocked.value) {
        showPiiModal.value = true;
        logUserInteraction(AmplitudeEventNames.PII_BLOCKED_MODAL_SHOWN, { patterns: piiResult.value?.reason }, coachingSessionId);
        return;
    }

    if (piiDetected.value) {
        showPiiModal.value = true;
        logUserInteraction(AmplitudeEventNames.PII_WARNING_MODAL_SHOWN, { patterns: piiResult.value?.reason }, coachingSessionId);
        return;
    }

    doSend();
};

const handlePiiModalConfirm = () => {
    logUserInteraction(AmplitudeEventNames.PII_WARNING_MODAL_SEND_ANYWAY, { patterns: piiResult.value?.reason }, coachingSessionId);
    acceptRisk();
    doSend();
};

const handlePiiEdited = (tier: number) => {
    logUserInteraction(AmplitudeEventNames.PII_WARNING_EDITED, { tier, patterns: piiResult.value?.reason }, coachingSessionId);
};

const handleKeyDown = () => emit("text-keydown");

const handleRecordingCancel = () => {
    recorder.value.stop({ abort: true });
    recorder.value.releaseStream();
};
const handleRecorderSilence = () => {
    handleRecordingCancel();
    openTestDialog();
};
const MAX_LEVEL_HISTORY = 100;

const handleRecorderLevel = (level: number) => {
    const history = recordingAudioLevelHistory.value;
    if (history.length >= MAX_LEVEL_HISTORY) {
        history.shift();
    }
    history.push(level);
};
const handleInitializing = () => (isInitializing.value = true);
const handleInitialized = () => (isInitializing.value = false);
const handleRecorderStart = () => {
    if (recordClickTime != null) {
        initToStartMs = Math.round(performance.now() - recordClickTime);
    }
    startRecordingTimer();
};
const handleInitializationFailed = () => (isInitializing.value = false);
const handleRecorderStopped = () => {
    stopRecordingTimer();
    recordingAudioLevelHistory.value = [];
    isRecording.value = false;
    logUserInteraction(AmplitudeEventNames.USER_RECORDING_ENDED, {}, coachingSessionUuid);

    clearTimeout(idleMicReleaseTimeout);
    idleMicReleaseTimeout = setTimeout(() => recorder.value.releaseStream(), IDLE_MIC_RELEASE_MS);
};
const handleVisibilityChange = () => {
    if (document.hidden && !isRecording.value) {
        recorder.value.releaseStream();
    }
};
const handleRecorderFinished = ({ blob, lengthMs, audioStats }: { blob: Blob; lengthMs: number; audioStats: AudioStats }) => {
    emit("recording-complete", {
        blob,
        recordingTime: msToDisplayTime(lengthMs),
        audioStats: {
            ...audioStats,
            ...(initToStartMs != null && { initToStartMs }),
        },
    });
    recordClickTime = null;
    initToStartMs = null;
};
const handleRecordingStart = async () => {
    // The mic affordances are unrendered when voice input is hidden, but the voice nudge and the
    // mic-permission dialog can still emit their way here.
    if (props.isDisabled || !voiceInputEnabled.value) {
        return;
    }

    if (micConsentBlocks(handleRecordingStart)) {
        return;
    }

    clearTimeout(idleMicReleaseTimeout);
    let promptOpened = false;
    recordClickTime = performance.now();
    initToStartMs = null;
    logUserInteraction(AmplitudeEventNames.USER_RECORDING_START, {}, coachingSessionUuid);

    stopDictation();

    isRecording.value = true;

    try {
        await recorder.value.start((micPermissionState: PermissionState) => {
            if (micPermissionState === "prompt") {
                promptOpened = true;
                showMicPermissionAide.value = true;
            }
        });

        // If the tab became hidden or the component unmounted while waiting
        // for getUserMedia, abort the recording and release the mic.
        if (document.hidden || !componentActive) {
            recorder.value.stop({ abort: true });
            isRecording.value = false;
            recorder.value.releaseStream();
            return;
        }

        /*
         * User may have chosen "I'd rather type" in the permission dialog
         * but still grant permission in the browser prompt. In this case,
         * we should not continue with a recording because the user
         * chose to use text.
         */
        if (promptOpened && showTextbox.value) {
            recorder.value.stop({ abort: true });
            isRecording.value = false;
            return;
        }
        emit("recording-start");
    } catch (e) {
        isRecording.value = false;

        if (e instanceof Error) {
            switch (e.name) {
                case "NotAllowedError":
                case "NotSupportedError":
                case "OverconstrainedError":
                case "NotFoundError":
                    openTestDialog();
                    break;
                default:
                    emit("recording-error");
            }
        }

        logError(e);
        logUserInteraction(AmplitudeEventNames.MIC_ERROR, props.userCoachExchangeId ? { uce_id: props.userCoachExchangeId } : {}, coachingSessionUuid);
    } finally {
        if (showMicPermissionAide.value) {
            showMicPermissionAide.value = false;
        }
    }
};

const openTestDialog = () => emitter.emit(CHAT_EVENT.OPEN_MIC_TEST_DIALOG);
const stopDictation = () => {
    if (audioPlayer.value) {
        audioPlayer.value.src = "";
        audioPlayer.value.pause();
    }
    isDictationPlaying.value = false;
    nextDictationChunkId.value = { messageId: undefined };
};

const cycleDictationSpeed = () => {
    useChatStore.cycleDictationSpeed();
    logUserInteraction(AmplitudeEventNames.COACH_DICTATION_SPEED_CHANGED, { speed: useChatStore.dictationSpeed }, coachingSessionUuid);
};

const skipDictation = () => {
    const currentMessageId = nextDictationChunkId.value.messageId;
    const currentSequenceNumber = nextDictationChunkId.value.sequenceNumber;
    stopDictation();
    if (currentMessageId) {
        skippedMessageIds.add(currentMessageId);
        delete dictationChunks.value[currentMessageId];
    }
    logUserInteraction(
        AmplitudeEventNames.COACH_DICTATION_SKIPPED,
        { message_id: currentMessageId, sequence_number: currentSequenceNumber, speed: useChatStore.dictationSpeed },
        coachingSessionUuid,
    );
};
const handleMicSelection = (event: MediaDeviceInfo) => recorder.value.setAudioInputDeviceId(event.deviceId);
// NOTE: For the future "File Uploads" feature see `chat.actions.file_upload`
// 1. UI Component here sends a `file_upload` action code (see `transcription_request` for an example) and gets a `signed_url` in response.
// 2. Client then uploads the file to the `signed_url` and gets the `file_url` or `object_id` in response.
// 3. Once the file is uploaded, the client holds this state and sends a `file_uploaded/object_id` property along with the message when clicking "Send".
const handlePaste = (event: ClipboardEvent) => {
    if (props.isPasteDisabled) {
        return;
    }

    if ((event.clipboardData?.getData("text/plain")?.length ?? 0) > DOCUMENT_LENGTH_THRESHOLD) {
        event.preventDefault();
        const NUM_BYTES = 100;
        const now = new Date();
        const content = event.clipboardData?.getData("text/plain") ?? "";
        const file = {
            name: `pasted-content-${now.toISOString()}.txt`,
            content: content,
            description: content.slice(0, NUM_BYTES).replace(/\n/g, " ") + "...",
        };
        useChatStore.addFile(file);
        emit("text-paste", file);
    }
};

function handleMicPermissionDialogClick({ useText } = { useText: true }) {
    showMicPermissionAide.value = false;
    showTextbox.value = useText;
}

function handleDictationToggle() {
    logUserInteraction(useChatStore.dictationOn ? "turned_dictation_off" : "turned_dictation_on", {}, coachingSessionUuid);
    useChatStore.toggleDictation();
}

// Document Uploader
const fileInput = useTemplateRef("fileInput");

const handleFileChange = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const files = target.files;
    if (files && files.length > 0) {
        await handleUpload([...files], "upload_button");
    }
};

const isUploading = ref(false);
const uploadErrors = ref<string[]>([]);

const emitUploadError = () => {
    emit("upload-error", uploadErrors.value.join("\n"));
};

const handleUpload = async (files: File[], source: string) => {
    if (props.isDisabled || props.isFileUploadLimit) {
        return;
    }
    isUploading.value = true;
    emit("upload-error", null);
    uploadErrors.value = [];

    let filesForUpload = normalizeMarkdownFiles(files, ALL_SUPPORTED_FILE_TYPES);

    let SUPPORTED_FILE_TYPES = [...DOCUMENT_TYPES];
    if (organizationDetails?.value.is_image_upload_enabled) {
        SUPPORTED_FILE_TYPES.push(...IMAGE_TYPES);
    }

    const unsupportedFiles = filesForUpload.filter((file) => !SUPPORTED_FILE_TYPES.includes(file.type));
    if (unsupportedFiles.length > 0) {
        const errorPrefix = "Unsupported file type";
        const errorMsg = `${errorPrefix}: ${unsupportedFiles.map((file) => file.name).join(", ")}`;
        uploadErrors.value.push(errorMsg);
        unsupportedFiles.forEach((file) => {
            logUserInteraction("upload_errored", { error_prefix: errorPrefix, error_message: errorMsg, file_size: file.size, file_type: file.type }, coachingSessionUuid);
        });
        filesForUpload = filesForUpload.filter((file) => !unsupportedFiles.includes(file));
    }

    // Use file size limits from API
    const oversizedFiles = filesForUpload.filter((file) => {
        const limit = fileSizeLimits.value[file.type] || defaultFileSizeLimit.value;
        return file.size > limit;
    });

    if (oversizedFiles.length > 0) {
        oversizedFiles.forEach((file) => {
            const limit = fileSizeLimits.value[file.type] || defaultFileSizeLimit.value;
            const limitMB = Math.floor(limit / (1024 * 1024));
            const errorPrefix = `File size exceeds ${limitMB} MB limit`;
            const errorMsg = `${errorPrefix}: ${file.name}`;
            uploadErrors.value.push(errorMsg);
            logUserInteraction("upload_errored", { error_prefix: errorPrefix, error_message: errorMsg, file_size: file.size, file_type: file.type }, coachingSessionUuid);
        });
        filesForUpload = filesForUpload.filter((file) => {
            const limit = fileSizeLimits.value[file.type] || defaultFileSizeLimit.value;
            return file.size <= limit;
        });
    }

    if (filesForUpload.length === 0) {
        emitUploadError();
        isUploading.value = false;
        return;
    }

    filesForUpload.forEach((file) => {
        logUserInteraction(
            "upload_started",
            {
                file_size: file.size,
                file_type: file.type,
                message_count: props.userMessageCount,
                source,
            },
            coachingSessionUuid,
        );
    });

    const result = await Promise.allSettled(filesForUpload.map((file) => uploadFile(file)));
    result.forEach((resp, index) => {
        const errorPrefix = "An error occurred while uploading file";
        const genericFileUploadError = `${errorPrefix}: ${filesForUpload[index].name}`;
        if (resp.status === "rejected") {
            if (isSessionEndedError(resp.reason)) {
                return;
            }
            if (resp.reason?.cause) {
                uploadErrors.value.push(resp.reason.message);
            } else {
                uploadErrors.value.push(genericFileUploadError);
            }
            logUserInteraction("upload_errored", { error_prefix: errorPrefix, error_message: resp.reason, file_type: filesForUpload[index].type }, coachingSessionUuid);
        } else {
            if (resp.value.error) {
                uploadErrors.value.push(genericFileUploadError);
                logUserInteraction("upload_errored", { error_prefix: errorPrefix, error_message: resp.value.error, file_type: filesForUpload[index].type }, coachingSessionUuid);
                return;
            }
            useChatStore.addFile(resp.value);
            if (resp.value.truncated) {
                const pct = Math.round(resp.value.truncation_ratio * 100);
                uploadErrors.value.push(`"${resp.value.name}" had too much text content and has been truncated — approximately ${pct}% of content was removed.`);
            }
            const file = filesForUpload[index];
            logUserInteraction(
                AmplitudeEventNames.UPLOAD_FINISHED,
                {
                    file_size: file?.size,
                    file_type: file?.type,
                },
                coachingSessionUuid,
            );
        }
    });
    if (uploadErrors.value.length > 0) {
        emitUploadError();
    }
    isUploading.value = false;
};

const handleFileUploadClick = () => {
    logUserInteraction(AmplitudeEventNames.UPLOAD_BUTTON_CLICKED, {}, coachingSessionUuid);
    fileInput.value?.click();
};

const logUploadCanceled = () => {
    logUserInteraction(AmplitudeEventNames.UPLOAD_CANCELED, {}, coachingSessionUuid);
};

const uploadFile = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    if (coachingSessionUuid) {
        formData.append("coaching_session_uuid", coachingSessionUuid);
    }

    const postUpload = (csrfToken: string) =>
        fetch("/api/chat/document/upload", {
            method: "POST",
            body: formData,
            headers: withRequestId({
                "X-CSRFToken": csrfToken,
            }),
        });

    try {
        let response = await postUpload(getCookie("csrftoken") ?? "");

        // Embedded webviews (MS Teams) can hold a stale csrftoken cookie that 403s every
        // POST. Fetch a server-issued token and retry once before surfacing the failure —
        // mirrors StreamingPostClient's CSRF recovery for the SSE path. Stands down when
        // the 403 carries a session-ended code, because then the global fetch wrapper
        // already retried and (on failure) reported the dialog.
        if (response.status === 403 && !(await wasGloballyHandled403(response))) {
            const freshToken = await refreshCsrfToken();
            if (freshToken) {
                response = await postUpload(freshToken);
            }
        }

        if (!response.ok) {
            if (response.status === 403) {
                // A 403 the refresh could not save is usually a genuine block (e.g. WAF), but it
                // is a dead or stale session when the body carries one of our codes -- in which
                // case the dialog is already saying so and this must not add a second, wrongly
                // worded message beside it (ENG-1667).
                let body: unknown = null;
                try {
                    body = await response.json();
                } catch {
                    body = null;
                }
                const sessionEndedCode = sessionEndedCodeFrom(body);
                if (sessionEndedCode) {
                    // Reported here as a backstop: the wrapper skips both the retry and the
                    // dialog when the token refresh itself is unavailable, and the results
                    // handler below discards this error on the assumption the dialog is
                    // showing. First report wins, so when the wrapper did report, this is a
                    // no-op. Mirrors StreamingPostClient._handleHttpError.
                    reportSessionEnded(sessionEndedCode, (body as { login_url?: string }).login_url ?? null);
                    throw new Error("Session ended", { cause: SESSION_ENDED_ERROR_CAUSE });
                }
                throw new Error(`Your file upload was blocked by our security system. Please [contact support](${$feedbackMailto}) if you believe this is an error.`, {
                    cause: WAF_ERROR_CAUSE,
                });
            }
            // Surface server error messages (e.g. PII rejection, file validation) instead of generic status text
            if (response.status === 400) {
                const errorData = await response.json().catch(() => null);
                if (errorData?.error) {
                    throw new Error(errorData.error, { cause: "server_error" });
                }
            }
            throw new Error(`Upload failed with status ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        return result;
    } catch (error) {
        console.error(`Upload failed for ${file.name}:`, error);
        throw error;
    }
};

const isMobileApp = usePage().props?.is_mobile_app;
const isDocumentUploadTooltipShown = ref(!isMobileApp && !hasSeenDocumentUploadFeatureTooltip.value);
const acceptedFileTypes = computed(() => {
    const documentTypes = ".txt,.csv,.pdf,.docx,.xlsx,.pptx,.md,.markdown";
    const imageTypes = ".jpg,.jpeg,.png,.webp,.gif";

    if (organizationDetails?.value.is_image_upload_enabled) {
        return `${documentTypes},${imageTypes}`;
    }
    return documentTypes;
});

const documentUploadTooltip = computed(() => {
    // Convert ".txt,.pdf,.docx" to ".txt, .pdf, .docx" for better readability
    const displayTypes = acceptedFileTypes.value.replace(/,/g, ", ");

    if (!hasSeenDocumentUploadFeatureTooltip.value) {
        return `Try uploading a ${displayTypes} file`;
    }
    if (props.isFileUploadLimit) {
        return `You've reached the maximum number of files allowed in this conversation (${FILE_UPLOAD_LIMIT})`;
    }
    return isUploading.value ? "Uploading document..." : `Upload file (${displayTypes})`;
});
watch(isDocumentUploadTooltipShown, (newValue, oldValue) => {
    if (!newValue && newValue !== oldValue) {
        hasSeenDocumentUploadFeatureTooltip.value = true;
    }
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";
[autocomplete="off"] div[data-lastpass-icon-root="true"] {
    display: none;
}

[autocomplete="off"] div[data-lastpass-infield="true"] {
    display: none;
}

textarea {
    resize: none;
}

/* Spec: one 24px input line snug under the card's own 24px padding, growing
   with content. Without field-sizing support it stays the legacy two-row box. */
html[data-theme="tonal"] #chat-input-textarea {
    display: block;
    margin-top: 0;
    min-height: 1.5rem;
    field-sizing: content;
}

html[data-theme="tonal"] .composer-input-area {
    min-height: 1.5rem;
}

textarea::-webkit-resizer {
    display: none;
}

textarea::placeholder {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.chat-message-controls--disabled {
    @apply opacity-60;
}

.chat-message-controls--disabled,
.chat-message-controls--disabled * {
    cursor: not-allowed;
}

.side-button {
    @apply size-10 transition-colors;
}

.side-button:disabled {
    @apply opacity-50;
}

/* The spec's composer icon buttons: an 8px radius, a neutral-50 hover fill,
   and neutral-700 glyphs that keep their color on hover. */
html[data-theme="tonal"] .side-button {
    border-radius: 0.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

/* Each side button carries two glyphs: the legacy art and the design-system
   asset, toggled per theme like BaseCheckbox's dual glyphs. The DS glyphs
   render at the sidebar's 16px scale inside the unchanged 40px hit area. */
.tonal-glyph {
    display: none;
}

html[data-theme="tonal"] .side-button :deep(.legacy-glyph) {
    display: none;
}

html[data-theme="tonal"] .side-button .tonal-glyph {
    display: block;
    width: 1.25rem;
    height: 1.25rem;
    color: var(--color-composer-side-button-foreground);
}

/* The spec renders the audio glyph 2px larger to optically balance the clip. */
html[data-theme="tonal"] .side-button .tonal-glyph--audio {
    width: 1.375rem;
    height: 1.375rem;
}

html[data-theme="tonal"] .side-button--active {
    background: var(--color-surface);
    box-shadow: inset 0 0 0 1.5px var(--color-control-accent);
}

html[data-theme="tonal"] .composer-bar {
    min-height: 2.5rem;
}

/* The buttons' own 40px boxes provide the breathing room. */
html[data-theme="tonal"] .composer-bar div.flex {
    gap: 0.125rem;
}

html[data-theme="tonal"] .side-button:not(.side-button--active):enabled:hover {
    background: var(--color-surface);
}

html[data-theme="tonal"] .side-button:not(.side-button--active):enabled:hover > div {
    background-color: transparent;
}

html[data-theme="tonal"] .side-button:not(.side-button--active):enabled:hover :deep(.side-button-background) {
    fill: transparent;
    opacity: 1;
}

html[data-theme="tonal"] .side-button:not(.side-button--active):enabled:hover :deep(.side-button-foreground) {
    stroke: var(--color-composer-side-button-foreground);
}

html[data-theme="tonal"] .side-button:not(.side-button--active):enabled:hover :deep(.side-button-foreground--fill) {
    fill: var(--color-composer-side-button-foreground);
}

:deep() {
    .side-button-foreground {
        @apply stroke-composer-side-button-foreground;
    }

    .side-button--active .side-button-background {
        @apply fill-surface-raised stroke-2 stroke-valence-primary-500;
    }

    .side-button--active .side-button-foreground {
        @apply stroke-valence-primary-500;
    }

    .side-button-foreground--fill {
        @apply fill-composer-side-button-foreground;
    }

    .side-button:not(.side-button--active) .side-button-background {
        @apply fill-transparent;
    }

    .side-button:not(.side-button--active):enabled:hover .side-button-foreground {
        @apply md:stroke-valence-primary-500;
    }

    .side-button:not(.side-button--active):enabled:hover .side-button-foreground--fill {
        @apply md:fill-valence-primary-500;
    }

    .side-button:not(.side-button--active):enabled:hover .side-button-background {
        @apply md:fill-valence-primary-500 md:opacity-25;
    }

    .submit-button {
        @apply shrink-0 text-accent h-10 w-10;
    }

    .submit-button-background {
        @apply transition-colors fill-accent;
    }

    .submit-button:hover .submit-button-background {
        @apply md:fill-accent-hover;
    }

    .submit-button:disabled .submit-button-background,
    .recorder-button--idle:disabled {
        @apply fill-composer-action-disabled;
    }

    .submit-button-foreground {
        @apply transition-colors stroke-ink-inverse fill-ink-inverse;
    }

    .submit-button:disabled .submit-button-foreground {
        @apply fill-valence-grey-750 stroke-valence-grey-750;
    }
}

.recorder-button {
    @apply flex items-center gap-1 text-[#FAFAFA] md:rounded-lgr md:py-3 md:px-6;
}

.recorder-button--idle {
    @apply md:hover:bg-accent-hover md:bg-accent;
}

.recorder-button--recording {
    @apply md:hover:bg-[#a70d19] md:bg-[#CF1322];
}

.recording-send-button {
    @apply shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-accent hover:bg-accent-hover transition-colors;
}

.recording-send-pulse {
    animation: send-pulse 2s ease-in-out infinite;
}

@keyframes send-pulse {
    0%,
    100% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.2);
    }
}

.recording-dot {
    @apply inline-block h-2.5 w-2.5 rounded-full bg-[#CF1322];
    animation: recording-pulse 1.5s ease-in-out infinite;
}

@keyframes recording-pulse {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.3;
    }
}

.dropzone {
    @apply rounded-2xl absolute inset-0 border-4 border-dashed border-control-accent transition-colors;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 150ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
